import isEmpty from 'lodash/isEmpty';
import forOwn from 'lodash/forOwn';
import remove from 'lodash/remove';

export default {
  namespaced: true,

  state: {
    contactsScrollDepth: 0,
    contactsViewMode: 'pyramid',
    contactsExpandedTree: {},
    relationField: 'recruited_by',
    activeCustomView: null,
  },

  mutations: {
    setContactsScrollDepth: (state, value) => {
      state.contactsScrollDepth = value;
    },
    setContactsViewMode: (state, value) => {
      state.contactsViewMode = value;
    },
    setContactsExpandedTree: (state, value) => {
      state.contactsExpandedTree = { ...value };
    },
    setActiveCustomView: (state, value) => {
      state.activeCustomView = value;
    },
  },

  actions: {
    /**
     * Upserts a contact into the expanded tree and reconciles its parent links.
     * Works for:
     *  - existing contacts (patches all occurrences + reconciles parents)
     *  - brand-new contacts (attaches under already-present parents or adds as root)
     */
    updateContactsExpandedTree({ commit, state, rootGetters }, contact) {
      if (!contact?.id || isEmpty(state.contactsExpandedTree)) return;

      const tree = { ...state.contactsExpandedTree };
      const relKey = rootGetters['roleRelationField'] || 'recruited_by';
      const nextParentIds = toIdArray(contact[relKey]);

      // Locate existing occurrences of this contact
      const nodes = findNodesById(tree, contact.id);

      if (nodes.length) {
        // Patch existing nodes in place
        nodes.forEach((n) => patchNode(n, contact));

        // Reconcile parent links (remove from old parents, add to new ones present in tree)
        if (nextParentIds && nextParentIds.length) {
          reconcileParents(tree, contact.id, nextParentIds, () =>
            cloneContactNode(contact)
          );
        }
      } else {
        // Brand-new contact
        let attached = false;

        // Try to attach under parents already present in the tree
        if (nextParentIds && nextParentIds.length) {
          attached = attachToExistingParents(tree, contact, nextParentIds);
        }

        // If no parents are present in the tree, add as a root
        if (!attached) {
          tree[contact.id] = cloneContactNode(contact);
        }
      }

      commit('setContactsExpandedTree', tree);
    },
  },
};

/* ---------- helpers ---------- */

// Shallow-merge contact into node, but keep the existing id
const patchNode = (node, contact) => {
  const patch = { ...contact };
  delete patch.id;
  Object.assign(node, patch);
};

// Fresh node instance for insertion into the tree
const cloneContactNode = (contact) => ({
  ...contact,
  expanded: false,
  loading: false,
  nested: Array.isArray(contact.nested) ? contact.nested : [],
});

// DFS over all nodes (roots in the object map + their nested children)
const walk = (tree, cb) => {
  const dfs = (node, parent) => {
    cb(node, parent);
    (node.nested || []).forEach((child) => dfs(child, node));
  };
  forOwn(tree || {}, (root) => dfs(root, null));
};

const findNodesById = (tree, id) => {
  const out = [];
  walk(tree, (node) => {
    if (node?.id === id) out.push(node);
  });
  return out;
};

const findFirstNode = (tree, id) => {
  let hit = null;
  walk(tree, (node) => {
    if (!hit && node?.id === id) hit = node;
  });
  return hit;
};

// All (parent, node) pairs where node.id === childId
const parentLinksOf = (tree, childId) => {
  const links = [];
  walk(tree, (node, parent) => {
    if (node?.id === childId && parent) {
      links.push({ parent, node });
    }
  });
  return links;
};

const ensureChild = (parentNode, childId, buildNode) => {
  parentNode.nested = Array.isArray(parentNode.nested) ? parentNode.nested : [];
  const exists = parentNode.nested.some((n) => n.id === childId);
  if (!exists) parentNode.nested.push(buildNode());
};

/**
 * Reconcile the set of parents for a child:
 *  - remove the child from parents not in nextParentIds
 *  - add the child under parents present in the tree
 */
const reconcileParents = (tree, childId, nextParentIds, buildNode) => {
  const nextSet = new Set(nextParentIds);

  // Detach from old parents
  parentLinksOf(tree, childId).forEach(({ parent, node }) => {
    const keep = parent.id && nextSet.has(parent.id);
    if (!keep) remove(parent.nested, (n) => n === node);
  });

  // Attach under new parents that already exist in the tree
  nextSet.forEach((pid) => {
    const parentNode = findFirstNode(tree, pid);
    if (parentNode) ensureChild(parentNode, childId, buildNode);
  });
};

// Normalize a single id or array of ids to a deduped array
const toIdArray = (val) => {
  if (!val) return null;
  const arr = Array.isArray(val) ? val : [val];
  return Array.from(new Set(arr.filter(Boolean)));
};

// Try to attach a new contact to parents already present in the tree
const attachToExistingParents = (tree, contact, parentIds) => {
  let count = 0;
  parentIds.forEach((pid) => {
    const parentNode = findFirstNode(tree, pid);
    if (parentNode) {
      ensureChild(parentNode, contact.id, () => cloneContactNode(contact));
      count++;
    }
  });
  return count > 0;
};
